import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import api from "@/lib/api";
import { Camera, MapPin, Clock, Sun } from "lucide-react";

const makeIcon = (hex) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${hex};width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 10px rgba(0,0,0,0.3);border:2px solid #fff;"></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });

const cityIcon = makeIcon("#C05640");
const spotIcon = makeIcon("#2563EB");

export default function Explore() {
  const [cities, setCities] = useState([]);
  const [spots, setSpots] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/data/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/data/photo-spots").then((r) => setSpots(r.data)).catch(() => {});
  }, []);

  const center = useMemo(() => [31.7917, -6.5], []);

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10">
      <p className="overline text-secondary">Interactive map</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">Explore Morocco</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Cities in terracotta, must-shoot photo spots in Majorelle blue. Tap a marker for details.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="h-[520px] overflow-hidden rounded-2xl border lg:col-span-8" data-testid="map-container">
          <MapContainer center={center} zoom={6} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {cities.map((c) => (
              <Marker key={c.id} position={[c.lat, c.lng]} icon={cityIcon}>
                <Popup>
                  <strong>{c.name}</strong>
                  <br />
                  {c.tagline}
                </Popup>
              </Marker>
            ))}
            {spots.map((s) => (
              <Marker
                key={s.id}
                position={[s.lat, s.lng]}
                icon={spotIcon}
                eventHandlers={{ click: () => setActive(s) }}
              >
                <Popup>
                  <strong>{s.name}</strong>
                  <br />
                  📷 {s.best_time}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="lg:col-span-4">
          <div className="rounded-2xl border bg-card p-6">
            <h3 className="flex items-center gap-2 font-display text-xl font-semibold">
              <Camera className="h-5 w-5 text-secondary" /> Photo spots
            </h3>
            <div className="mt-4 max-h-[420px] space-y-3 overflow-y-auto pr-1" data-testid="spots-list">
              {spots.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActive(s)}
                  data-testid={`spot-${s.id}`}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors hover:bg-muted ${
                    active?.id === s.id ? "border-secondary bg-secondary/5" : ""
                  }`}
                >
                  <img src={s.image} alt={s.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="min-w-0">
                    <p className="truncate font-medium">{s.name}</p>
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" /> {s.city}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {active && (
        <div className="mt-6 grid grid-cols-1 gap-6 overflow-hidden rounded-2xl border bg-card sm:grid-cols-2" data-testid="spot-detail">
          <img src={active.image} alt={active.name} className="h-full max-h-[320px] w-full object-cover" />
          <div className="p-7">
            <h3 className="font-display text-2xl font-bold">{active.name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{active.city}</p>
            <div className="mt-4 space-y-3 text-sm">
              <p className="flex items-start gap-2"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-secondary" /> <span><strong>Best time:</strong> {active.best_time}</span></p>
              <p className="flex items-start gap-2"><Sun className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span><strong>Golden hour:</strong> {active.golden_hour}</span></p>
              <p className="text-muted-foreground">{active.lighting_notes}</p>
              <p className="rounded-lg bg-secondary/10 p-3 text-secondary"><strong>Tip:</strong> {active.instagram_tip}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
